/**
 * Performance Monitoring Utilities
 * Provides tools for monitoring and optimizing application performance
 */

import React from 'react';
import { config } from '../config/environment';

/**
 * Performance metrics collector
 */
class PerformanceMonitor {
  constructor() {
    this.metrics = new Map();
    this.observers = [];
    this.isEnabled =
      config.features.enableAnalytics && !config.environment.isTest;

    if (this.isEnabled) {
      this.initializeObservers();
    }
  }

  /**
   * Initialize performance observers
   */
  initializeObservers() {
    // Navigation timing observer
    if ('PerformanceObserver' in window) {
      try {
        const navigationObserver = new PerformanceObserver(list => {
          const entries = list.getEntries();
          entries.forEach(entry => {
            if (entry.entryType === 'navigation') {
              this.recordNavigationMetrics(entry);
            }
          });
        });

        navigationObserver.observe({ entryTypes: ['navigation'] });
        this.observers.push(navigationObserver);
      } catch (error) {
        console.warn('Navigation observer not supported:', error);
      }

      // Resource timing observer
      try {
        const resourceObserver = new PerformanceObserver(list => {
          const entries = list.getEntries();
          entries.forEach(entry => {
            if (entry.entryType === 'resource') {
              this.recordResourceMetrics(entry);
            }
          });
        });

        resourceObserver.observe({ entryTypes: ['resource'] });
        this.observers.push(resourceObserver);
      } catch (error) {
        console.warn('Resource observer not supported:', error);
      }

      // Largest Contentful Paint observer
      try {
        const lcpObserver = new PerformanceObserver(list => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1];
          this.recordMetric('lcp', lastEntry.startTime);
        });

        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
        this.observers.push(lcpObserver);
      } catch (error) {
        console.warn('LCP observer not supported:', error);
      }

      // First Input Delay observer
      try {
        const fidObserver = new PerformanceObserver(list => {
          const entries = list.getEntries();
          entries.forEach(entry => {
            this.recordMetric('fid', entry.processingStart - entry.startTime);
          });
        });

        fidObserver.observe({ entryTypes: ['first-input'] });
        this.observers.push(fidObserver);
      } catch (error) {
        console.warn('FID observer not supported:', error);
      }
    }
  }

  /**
   * Record navigation metrics
   */
  recordNavigationMetrics(entry) {
    const metrics = {
      dns: entry.domainLookupEnd - entry.domainLookupStart,
      tcp: entry.connectEnd - entry.connectStart,
      ssl:
        entry.secureConnectionStart > 0
          ? entry.connectEnd - entry.secureConnectionStart
          : 0,
      ttfb: entry.responseStart - entry.requestStart,
      download: entry.responseEnd - entry.responseStart,
      domParse:
        entry.domContentLoadedEventEnd - entry.domContentLoadedEventStart,
      domReady: entry.domContentLoadedEventEnd - entry.navigationStart,
      loadComplete: entry.loadEventEnd - entry.navigationStart,
    };

    Object.entries(metrics).forEach(([key, value]) => {
      this.recordMetric(`navigation.${key}`, value);
    });
  }

  /**
   * Record resource metrics
   */
  recordResourceMetrics(entry) {
    const resourceType = this.getResourceType(entry.name);
    const loadTime = entry.responseEnd - entry.startTime;

    this.recordMetric(`resource.${resourceType}.loadTime`, loadTime);
    this.recordMetric(`resource.${resourceType}.size`, entry.transferSize || 0);
  }

  /**
   * Get resource type from URL
   */
  getResourceType(url) {
    if (url.includes('.js')) return 'javascript';
    if (url.includes('.css')) return 'stylesheet';
    if (url.match(/\.(jpg|jpeg|png|gif|webp|svg)$/)) return 'image';
    if (url.includes('/api/')) return 'api';
    return 'other';
  }

  /**
   * Record a custom metric
   */
  recordMetric(name, value, tags = {}) {
    if (!this.isEnabled) return;

    const metric = {
      name,
      value,
      timestamp: Date.now(),
      tags: {
        environment: config.app.environment,
        version: config.app.version,
        ...tags,
      },
    };

    this.metrics.set(`${name}_${Date.now()}`, metric);

    // Log in development
    if (config.environment.isDevelopment) {
      console.log(`Performance metric: ${name} = ${value}`, tags);
    }

    // Send to analytics
    this.sendToAnalytics(metric);
  }

  /**
   * Send metrics to analytics service
   */
  sendToAnalytics(metric) {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'performance_metric', {
        metric_name: metric.name,
        metric_value: Math.round(metric.value),
        ...metric.tags,
      });
    }
  }

  /**
   * Get all recorded metrics
   */
  getMetrics() {
    return Array.from(this.metrics.values());
  }

  /**
   * Get metrics by name pattern
   */
  getMetricsByPattern(pattern) {
    return this.getMetrics().filter(metric => metric.name.includes(pattern));
  }

  /**
   * Clear all metrics
   */
  clearMetrics() {
    this.metrics.clear();
  }

  /**
   * Cleanup observers
   */
  cleanup() {
    this.observers.forEach(observer => observer.disconnect());
    this.observers = [];
  }
}

// Global performance monitor instance
export const performanceMonitor = new PerformanceMonitor();

/**
 * Measure function execution time
 */
export const measureExecutionTime = (fn, name) => {
  return async (...args) => {
    const startTime = performance.now();

    try {
      const result = await fn(...args);
      const executionTime = performance.now() - startTime;

      performanceMonitor.recordMetric(`function.${name}`, executionTime);

      return result;
    } catch (error) {
      const executionTime = performance.now() - startTime;
      performanceMonitor.recordMetric(`function.${name}.error`, executionTime);
      throw error;
    }
  };
};

/**
 * HOC for measuring component render time
 */
export const withPerformanceMonitoring = (WrappedComponent, componentName) => {
  return React.forwardRef((props, ref) => {
    const renderStartTime = React.useRef();

    // Measure render start
    renderStartTime.current = performance.now();

    React.useEffect(() => {
      // Measure render complete
      const renderTime = performance.now() - renderStartTime.current;
      performanceMonitor.recordMetric(
        `component.${componentName}.render`,
        renderTime
      );
    });

    return <WrappedComponent {...props} ref={ref} />;
  });
};

/**
 * Hook for measuring component lifecycle
 */
export const usePerformanceMonitoring = componentName => {
  const mountTime = React.useRef();
  const renderCount = React.useRef(0);

  React.useEffect(() => {
    mountTime.current = performance.now();

    return () => {
      const startTime = mountTime.current;
      const rendersAtUnmount = renderCount.current;
      const lifetimeMs = performance.now() - startTime;
      performanceMonitor.recordMetric(
        `component.${componentName}.lifetime`,
        lifetimeMs
      );
      performanceMonitor.recordMetric(
        `component.${componentName}.renders`,
        rendersAtUnmount
      );
    };
  }, [componentName]);

  React.useEffect(() => {
    renderCount.current++;
  });

  return {
    recordMetric: (name, value, tags) => {
      performanceMonitor.recordMetric(
        `component.${componentName}.${name}`,
        value,
        tags
      );
    },
  };
};

/**
 * Measure API call performance
 */
export const measureApiCall = (apiCall, endpoint) => {
  return async (...args) => {
    const startTime = performance.now();

    try {
      const result = await apiCall(...args);
      const responseTime = performance.now() - startTime;

      performanceMonitor.recordMetric('api.response_time', responseTime, {
        endpoint,
        status: 'success',
      });

      return result;
    } catch (error) {
      const responseTime = performance.now() - startTime;

      performanceMonitor.recordMetric('api.response_time', responseTime, {
        endpoint,
        status: 'error',
        error: error.message,
      });

      throw error;
    }
  };
};

/**
 * Memory usage monitoring
 */
export const monitorMemoryUsage = () => {
  if ('memory' in performance) {
    const memory = performance.memory;

    performanceMonitor.recordMetric('memory.used', memory.usedJSHeapSize);
    performanceMonitor.recordMetric('memory.total', memory.totalJSHeapSize);
    performanceMonitor.recordMetric('memory.limit', memory.jsHeapSizeLimit);
  }
};

/**
 * Bundle size monitoring
 */
export const monitorBundleSize = () => {
  if ('getEntriesByType' in performance) {
    const resources = performance.getEntriesByType('resource');

    let totalJSSize = 0;
    let totalCSSSize = 0;

    resources.forEach(resource => {
      if (resource.name.includes('.js')) {
        totalJSSize += resource.transferSize || 0;
      } else if (resource.name.includes('.css')) {
        totalCSSSize += resource.transferSize || 0;
      }
    });

    performanceMonitor.recordMetric('bundle.javascript_size', totalJSSize);
    performanceMonitor.recordMetric('bundle.css_size', totalCSSSize);
  }
};

/**
 * Core Web Vitals monitoring
 */
export const monitorCoreWebVitals = () => {
  // Cumulative Layout Shift
  let clsValue = 0;
  let clsEntries = [];

  if ('PerformanceObserver' in window) {
    try {
      const clsObserver = new PerformanceObserver(list => {
        for (const entry of list.getEntries()) {
          if (!entry.hadRecentInput) {
            clsEntries.push(entry);
            clsValue += entry.value;
          }
        }

        performanceMonitor.recordMetric('cwv.cls', clsValue);
      });

      clsObserver.observe({ entryTypes: ['layout-shift'] });
    } catch (error) {
      console.warn('CLS observer not supported:', error);
    }
  }
};

/**
 * Performance report generator
 */
export const generatePerformanceReport = () => {
  const metrics = performanceMonitor.getMetrics();

  const report = {
    timestamp: new Date().toISOString(),
    environment: config.app.environment,
    version: config.app.version,
    metrics: {
      navigation: {},
      resources: {},
      components: {},
      api: {},
      memory: {},
      bundle: {},
    },
  };

  metrics.forEach(metric => {
    const [category] = metric.name.split('.');
    if (report.metrics[category]) {
      report.metrics[category][metric.name] = {
        value: metric.value,
        timestamp: metric.timestamp,
        tags: metric.tags,
      };
    }
  });

  return report;
};

/**
 * Initialize performance monitoring
 */
export const initializePerformanceMonitoring = () => {
  if (!config.features.enableAnalytics) return;

  // Monitor core web vitals
  monitorCoreWebVitals();

  // Monitor memory usage periodically
  setInterval(monitorMemoryUsage, 30000); // Every 30 seconds

  // Monitor bundle size on load
  if (document.readyState === 'complete') {
    monitorBundleSize();
  } else {
    window.addEventListener('load', monitorBundleSize);
  }

  // Cleanup on page unload
  window.addEventListener('beforeunload', () => {
    performanceMonitor.cleanup();
  });
};
